/* ==========================================================================
   Noveliya — Gallery custom element
   Handles two behaviours:
     1. Mobile carousel: IntersectionObserver keeps line indicators in sync
        as the user swipes through scroll-snap items.
     2. Arrow navigation: prev/next buttons scroll the carousel one step.
   ========================================================================== */

if (!customElements.get('nv-gallery')) {
  customElements.define(
    'nv-gallery',
    class NvGallery extends HTMLElement {
      connectedCallback() {
        this.track        = this.querySelector('.nv-gallery__track');
        this.items        = [...this.querySelectorAll('.nv-gallery__item')];
        this.dots         = [...this.querySelectorAll('.nv-gallery__dot')];
        this._currentIndex = 0;

        this._initDotSync();
        this._initArrows();
        this._initDotClicks();
      }

      /* ── Dot sync (mobile carousel) ───────────────────────────────────── */

      _initDotSync() {
        if (!this.track || !this.dots.length || !this.items.length) return;

        this._io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const idx = this.items.indexOf(entry.target);
              if (idx < 0) return;
              this._currentIndex = idx;
              this.dots.forEach((dot, i) => {
                dot.classList.toggle('is-active', i === idx);
                dot.setAttribute('aria-selected', i === idx ? 'true' : 'false');
              });
            });
          },
          { root: this.track, threshold: 0.5 }
        );

        this.items.forEach((item) => this._io.observe(item));
      }

      /* ── Arrow navigation ─────────────────────────────────────────────── */

      _initArrows() {
        this.prevBtn = this.querySelector('[data-nv-gallery-prev]');
        this.nextBtn = this.querySelector('[data-nv-gallery-next]');
        this.prevBtn?.addEventListener('click', () => this._step(-1));
        this.nextBtn?.addEventListener('click', () => this._step(1));
      }

      _step(dir) {
        const count = this.items.length;
        if (!count) return;
        const target = (this._currentIndex + dir + count) % count;
        this._goTo(target);
      }

      /* ── Dot clicks ───────────────────────────────────────────────────── */

      _initDotClicks() {
        this.dots.forEach((dot, i) => {
          dot.addEventListener('click', () => this._goTo(i));
        });
      }

      /* ── Go to index ──────────────────────────────────────────────────── */

      _goTo(index) {
        const item = this.items[index];
        if (!item) return;
        item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }

      disconnectedCallback() {
        this._io?.disconnect();
      }
    }
  );
}
